import React, { useEffect, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ReportFileExports from 'components/Reports/FileExports';
import ApiHelper from 'utils/apiHelper';
import { gridSpacing } from 'utils/constants';
import DeviceTypeDevicesDialog from './DeviceTypeDevicesDialog';

const initialState = {
    os: { values: [], labels: [] },
    type: { values: [], labels: [] }
};

const ReportsDeviceType = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [groupId, setGroupId] = useState(0);
    const [policyId, setPolicyId] = useState(0);
    const [groupsData, setGroupsData] = useState([]);
    const [policiesData, setPoliciesData] = useState([]);
    const [deviceTypeData, setDeviceTypeData] = useState(initialState);
    const [showDevicesByOs, setShowDevicesByOs] = useState(false);
    const [showDevicesByType, setShowDevicesByType] = useState(false);
    const [type, setType] = useState('os');
    const [osLabel, setOSLabel] = useState('');
    const [typeLabel, setTypeLabel] = useState('');
    const [filtersData, setFiltersData] = useState([]);

    useEffect(() => {
        getGroups();
        getPolicies();
    }, []);

    const getGroups = () => {
        ApiHelper.get('/api/reports/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch();
    };

    const getPolicies = () => {
        ApiHelper.get('/api/management/policy/')
            .then((res) => {
                setPoliciesData(res.data);
            })
            .catch();
    };

    const getDevicesTypeData = () => {
        setLoading(true);
        ApiHelper.post('/api/reports/devices_type/', {
            group_id: groupId === 0 ? '' : groupId.toString(),
            policy_id: policyId === 0 ? '' : policyId.toString()
        })
            .then((res) => {
                setDeviceTypeData(res.data);
                setLoading(false);
                let groupName = 'Any';
                let policyName = 'Any';
                for (let i = 0; i < groupsData.length; i++) {
                    if (groupsData[i].id === groupId) {
                        groupName = groupsData[i].name;
                    }
                }
                for (let i = 0; i < policiesData.length; i++) {
                    if (policiesData[i].id === policyId) {
                        policyName = policiesData[i].name;
                    }
                }
                const temp = [
                    { label: 'Group Name', value: groupName },
                    { label: 'Policy Name', value: policyName }
                ];
                setFiltersData(temp);
            })
            .catch(() => {
                setDeviceTypeData(initialState);
                setLoading(false);
                toast.warning('There is no device data');
            });
    };

    const seriesOfDevicesType = deviceTypeData['type']['values'];
    const optionsOfDevicesType = {
        chart: {
            toolbar: {
                show: false
            },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // this will print mango, apple etc. when clicked on respective datapoint
                    setShowDevicesByType(true);
                    setTypeLabel(deviceTypeData.type.labels[config.dataPointIndex]);
                    setType('type');
                },
                dataPointMouseEnter: (event) => {
                    event.target.style.cursor = 'pointer';
                }
            }
        },
        title: {
            text: 'Devices by Type',
            style: {
                fontSize: '16px',
                fontWeight: '500'
            }
        },
        labels: deviceTypeData.type.labels,
        colors: ['#2F3EB1', '#E38B03', '#A303E3'],
        legend: { position: 'bottom' }
    };

    const seriesOfDevicesOS = deviceTypeData['os']['values'];
    const optionsOfDevicesOS = {
        chart: {
            toolbar: {
                show: false
            },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // this will print mango, apple etc. when clicked on respective datapoint
                    setShowDevicesByOs(true);
                    setOSLabel(deviceTypeData.os.labels[config.dataPointIndex]);
                    setType('os');
                },
                dataPointMouseEnter: (event) => {
                    event.target.style.cursor = 'pointer';
                }
            }
        },
        title: {
            text: 'Devices by OS',
            style: {
                fontSize: '16px',
                fontWeight: '500'
            }
        },
        labels: deviceTypeData.os.labels,
        legend: { position: 'bottom' }
    };

    const csvData = [
        ['Devices by OS'],
        deviceTypeData.os.labels,
        deviceTypeData.os.values,
        [''],
        ['Devices by type'],
        deviceTypeData.type.labels,
        deviceTypeData.type.values
    ];

    return (
        <MainCard title="Activity Reports Management" cardHeaderTitle="Devices by Type and OS" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={10}>
                    <Typography sx={{ fontSize: '16px' }}>Provides a pie chart showing breakdown of device types</Typography>
                </Grid>
                {deviceTypeData.type.values.length > 0 && (
                    <Grid item xs={2}>
                        <ReportFileExports
                            elementId="deviceType"
                            reportTitle="Devices by Type and OS"
                            downloadFileName="devices_by_type_and_os_report"
                            downloadFileOption="all"
                            data={csvData}
                            headers={[]}
                            filtersData={filtersData}
                        />
                    </Grid>
                )}
                <Grid item xs={3}>
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Group</InputLabel>
                                    <Select label="Select Group" value={groupId} name="group" onChange={(e) => setGroupId(e.target.value)}>
                                        <MenuItem value={0}>Any</MenuItem>
                                        {groupsData.length > 0 &&
                                            groupsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Policy</InputLabel>
                                    <Select
                                        label="Select Policy"
                                        value={policyId}
                                        name="policy"
                                        onChange={(e) => setPolicyId(e.target.value)}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {policiesData.length > 0 &&
                                            policiesData.map((data, key) => (
                                                <MenuItem value={data.policyid} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={getDevicesTypeData}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={9}>
                    <Grid container spacing={gridSpacing} id="deviceType">
                        {loading === true ? (
                            <Grid item xs={12}>
                                <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                    Please wait...
                                </Typography>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={6}>
                                    {deviceTypeData.os.values.length === 0 ? (
                                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                            No data for the devices by os
                                        </Typography>
                                    ) : (
                                        <Chart options={optionsOfDevicesOS} series={seriesOfDevicesOS} type="pie" height={500} />
                                    )}
                                    {showDevicesByOs && (
                                        <DeviceTypeDevicesDialog
                                            groupId={groupId}
                                            policyId={policyId}
                                            deviceType={type}
                                            columnIndex={osLabel}
                                            groupsData={groupsData}
                                            policiesData={policiesData}
                                            onClose={() => setShowDevicesByOs(false)}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={6}>
                                    {deviceTypeData.type.values.length === 0 ? (
                                        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                            No data for the devices by type
                                        </Typography>
                                    ) : (
                                        <Chart options={optionsOfDevicesType} series={seriesOfDevicesType} type="pie" height={500} />
                                    )}
                                    {showDevicesByType && (
                                        <DeviceTypeDevicesDialog
                                            groupId={groupId}
                                            policyId={policyId}
                                            deviceType={type}
                                            columnIndex={typeLabel}
                                            groupsData={groupsData}
                                            policiesData={policiesData}
                                            onClose={() => setShowDevicesByType(false)}
                                        />
                                    )}
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ReportsDeviceType;
